import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { BasicTextEditor, StatusTag } from '@factorialco/factorial-one-react/dist/experimental'
import { BasicTextEditorHandle } from '@factorialco/factorial-one-react/dist/experimental'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { MeetingSkeleton } from 'modules/meetings/components/Skeletons/MeetingSkeleton'
import { useBasicTextEditorConfig } from 'modules/meetings/lib/hooks/useBasicTextEditorConfig'
import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useMeetingNotes } from 'modules/meetings/lib/hooks/useMeetingNotes'
import { formatDateTimeToString } from 'modules/meetings/lib/utils/dates'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

interface MeetingNotesProps {
  textEditorRef: React.RefObject<BasicTextEditorHandle> | null | undefined
  meetingState: 'pre' | 'post' | null | undefined
}

export const MeetingNotes = ({ textEditorRef, meetingState }: MeetingNotesProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { id: meetingId } = useParams<{ id: string }>()
  const { notes, setNotes, isSavingNotes } = useMeetingNotes()
  const { page, loading } = useMeetingDetails()

  const { labels, aiBlockConfig } = useBasicTextEditorConfig({
    meetingId,
    meetingState: meetingState || undefined,
    setNotes,
  })

  const isStillLoading = loading || !page || notes === null || !meetingState

  if (isStillLoading) return <MeetingSkeleton />

  return (
    <Box
      gap="s16"
      height="full"
      flexGrow
      style={{
        width: '100%',
        cursor: 'text',
      }}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        gap="s8"
        justifyContent="spaceBetween"
        paddingX="s40"
        paddingTop="s24"
      >
        <StatusTag text={i18n.t('meetings.notes.private_notes')} variant="neutral" />
        {page?.updatedAt && (
          <Text size="100" color="grey800">
            {isSavingNotes
              ? i18n.t('meetings.notes.autosaving')
              : formatDateTimeToString(page?.updatedAt, locale)}
          </Text>
        )}
      </Box>
      <Box paddingRight="s24" paddingBottom="s16" flexGrow className={styles.hiddenScrollBar}>
        <BasicTextEditor
          ref={textEditorRef}
          labels={labels}
          placeholder={i18n.t('meetings.notes.placeholder')}
          onChange={({ json }) => {
            setNotes(JSON.stringify(json))
          }}
          initialEditorState={{
            content: notes && notes.trim() !== '' ? JSON.parse(notes) : undefined,
          }}
          aiBlockConfig={aiBlockConfig}
        />
      </Box>
    </Box>
  )
}
